<template>
  <b-form-group>
    <b-row no-gutters>
      <b-col
        cols="12"
        class="mb-2"
      >
        <b-input-group>
          <b-input
            :value="name"
            placeholder="Field name"
            @input="$emit('update:name', $event)"
          />
        </b-input-group>
      </b-col>

      <b-col
        cols="12"
        md="6"
        xl="3"
        class="mb-2 pr-sm-1"
      >
        <b-button-group class="w-100">
          <b-dropdown
            :text="functionSelected.text"
            class="w-100"
          >
            <b-dropdown-item
              v-for="func in functions"
              :key="func.value"
              @click="functionSelected=func"
            >
              {{ func.text }}
            </b-dropdown-item>
          </b-dropdown>

          <b-dropdown
            v-if="showFields"
            variant="primary"
            text="Insert field"
            class="w-100"
          >
            <b-dropdown-item
              v-for="numField in numericMonetaryFields"
              :key="numField.column"
              @click="addFunctionToDefinition(numField.column)"
            >
              {{ numField.name }}
            </b-dropdown-item>
          </b-dropdown>
          <b-button
            v-else
            variant="primary"
            class="w-100"
            @click="addFunctionToDefinition(numericMonetaryFields[0].column)"
          >
            Insert field
          </b-button>
        </b-button-group>
      </b-col>

      <b-col
        cols="12"
        md="6"
        xl="3"
        class="mb-2 pl-sm-1 pr-xl-1"
      >
        <b-button-group class="w-100">
          <FieldOperator
            v-for="operator in operators"
            :key="operator"
            :operator="operator"
            @click="addOperatorToDefinitions(operator)"
          />
        </b-button-group>
      </b-col>

      <b-col
        cols="12"
        md="6"
        xl="3"
        class="mb-2 pr-sm-1 pl-xl-1"
      >
        <b-input-group>
          <b-input
            v-model="number"
            type="number"
            step="any"
          />
          <b-input-group-append>
            <b-button
              block
              variant="info"
              @click="addNumberToDefinitions"
            >
              Insert number
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </b-col>

      <b-col
        cols="12"
        md="6"
        xl="3"
        class="mb-2 pl-sm-1"
      >
        <b-input-group
          prepend="Unit"
        >
          <b-input
            :value="unit"
            :disabled="isMonetary"
            :style="{'width': '3.5rem'}"
            @input="$emit('update:unit', $event)"
          />
          <b-input-group-append>
            <b-input-group-text>
              <b-checkbox
                v-model="checkFieldCalculationIsMonetary"
                name="is-monetary"
              >
                Monetary
              </b-checkbox>
            </b-input-group-text>
          </b-input-group-append>
        </b-input-group>
      </b-col>
    </b-row>

    <p v-if="definitions.length === 0">
      <i>Use the controls above to write a calculation formula</i>
    </p>

    <div class="d-flex">
      <div
        v-for="(definition, defIndex) in definitions"
        :key="`v-field-definition-${defIndex}`"
        class="mb-2 mr-1"
        @click="definitions.splice(defIndex, 1)"
      >
        <FieldOperator
          v-if="definition.type === OPERATOR"
          :operator="definition.operator"
        />
        <FieldButton
          v-else
          :definition="definition"
        />
      </div>
    </div>
  </b-form-group>
</template>

<script lang="ts">

import Vue, { PropType } from 'vue'
import Component from 'vue-class-component'

import {
  FieldCalculationDefinition,
  FieldCalculationFunctionTypes,
  FieldCalculationOperatorType,
  FieldCalculationOperatorTypes,
  FieldCalculationTypes,
} from '@/types'
import {
  MONETARY_UNIT,
  FIELD_CALC_FUNCTIONS,
} from '@/constants'
import FieldOperator from '@/components/builder/FieldCalculator/FieldOperator.vue'
import FieldButton from '@/components/builder/FieldCalculator/FieldButton.vue'

const components = {
  FieldOperator,
  FieldButton,
}

const props = {
  name: {
    type: String,
    required: true,
  },
  definitions: {
    type: Array as PropType<FieldCalculationDefinition[]>,
    required: true,
  },
  unit: {
    type: String,
    required: true,
  },
  isMonetary: {
    type: Boolean,
    required: true,
  },
}

@Component({ components })
export default class FieldDefinitions extends Vue.extend({ props }) {
  operators = [
    FieldCalculationOperatorTypes.PLUS,
    FieldCalculationOperatorTypes.MINUS,
    FieldCalculationOperatorTypes.MULTIPLY,
    FieldCalculationOperatorTypes.DIVIDE_BY,
    FieldCalculationOperatorTypes.OPEN_PARENTHESIS,
    FieldCalculationOperatorTypes.CLOSE_PARENTHESIS,
  ]

  number = 0
  functionSelected = FIELD_CALC_FUNCTIONS[0]
  functions = FIELD_CALC_FUNCTIONS
  OPERATOR = FieldCalculationTypes.OPERATOR

  get numericMonetaryFields () {
    return this.$typedStore.visualisationApp.numericMonetaryFields
  }

  get checkFieldCalculationIsMonetary () {
    return this.isMonetary
  }

  set checkFieldCalculationIsMonetary (isMonetary) {
    if (isMonetary) this.$emit('update:unit', MONETARY_UNIT)
    this.$emit('update:isMonetary', isMonetary)
  }

  get showFields () {
    return this.functionSelected.value !== FieldCalculationFunctionTypes.COUNT
  }

  addFunctionToDefinition (column: string) {
    this.definitions.push({
      type: FieldCalculationTypes.FUNCTION,
      function: this.functionSelected.value,
      column,
    })
  }

  addOperatorToDefinitions (operator: FieldCalculationOperatorType) {
    this.definitions.push({
      type: FieldCalculationTypes.OPERATOR,
      operator,
    })
  }

  addNumberToDefinitions () {
    this.definitions.push({
      type: FieldCalculationTypes.NUMBER,
      number: String(this.number),
    })
  }
}

</script>
